import React, { useEffect, useMemo, useState } from "react";

import styled from "styled-components";

import { MANUSCRIPT_COMPILER_OPTIONS, MANUSCRIPT_DEFAULT_COMPILER } from "utils/constants/manuscript";

export function useManuscriptSettings({
    manuscript,
    manuscriptSettingsMutation,
    onError,
    onSaved,
    treeNodes,
    treeQuery,
}) {
    const [settingsTab, setSettingsTab] = useState("compiler");
    const [settingsRootNodeId, setSettingsRootNodeId] = useState("");
    const [settingsCompiler, setSettingsCompiler] = useState(MANUSCRIPT_DEFAULT_COMPILER);
    const compilerOptions = MANUSCRIPT_COMPILER_OPTIONS;
    const rootCandidateNodes = useMemo(
        () => treeNodes.filter((node) => node.node_type === "text" && (node.name || "").toLowerCase().endsWith(".tex")),
        [treeNodes],
    );

    useEffect(() => {
        const rootNodeId = manuscript?.root_tree_node_id;
        if (rootNodeId != null) {
            setSettingsRootNodeId(String(rootNodeId));
        }
        const compiler = manuscript?.default_compiler;
        if (compiler) {
            setSettingsCompiler(compiler);
        }
    }, [manuscript?.root_tree_node_id, manuscript?.default_compiler]);

    useEffect(() => {
        if (!rootCandidateNodes.length) {
            setSettingsRootNodeId("");
            return;
        }
        const exists = rootCandidateNodes.some((node) => String(node.id) === settingsRootNodeId);
        if (!exists) {
            setSettingsRootNodeId(String(rootCandidateNodes[0].id));
        }
    }, [rootCandidateNodes, settingsRootNodeId]);

    const saveProjectSettings = async () => {
        try {
            const payload = {};
            if (settingsRootNodeId) {
                payload.root_tree_node_id = Number(settingsRootNodeId);
            }
            if (settingsCompiler) {
                payload.default_compiler = settingsCompiler;
            }
            await manuscriptSettingsMutation.mutateAsync(payload);
            await treeQuery.refetch();
            onSaved?.();
        } catch (error) {
            onError?.(error);
        }
    };

    return {
        compilerOptions,
        rootCandidateNodes,
        saveProjectSettings,
        setSettingsCompiler,
        setSettingsRootNodeId,
        setSettingsTab,
        settingsCompiler,
        settingsRootNodeId,
        settingsTab,
    };
}

function ManuscriptSettingsModal({
    ActionButton,
    compilerOptions,
    Dialog,
    DialogBody,
    DialogHeader,
    isSaving,
    onClose,
    onCompilerChange,
    onRootNodeChange,
    onSave,
    onSettingsTabChange,
    Overlay,
    rootCandidateNodes,
    settingsCompiler,
    settingsRootNodeId,
    settingsTab,
}) {
    return (
        <Overlay onClick={onClose}>
            <Dialog
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                <DialogHeader>
                    <div>프로젝트 설정</div>
                    <ActionButton type="button" onClick={onClose}>
                        닫기
                    </ActionButton>
                </DialogHeader>
                <DialogBody>
                    <SettingsTabs>
                        <SettingsTabButton
                            type="button"
                            $active={settingsTab === "compiler"}
                            onClick={() => onSettingsTabChange("compiler")}
                        >
                            컴파일러
                        </SettingsTabButton>
                    </SettingsTabs>
                    {settingsTab === "compiler" ? (
                        <SettingsForm>
                            <SettingsRow>
                                <SettingsLabel>주 문서</SettingsLabel>
                                <SettingsSelect value={settingsRootNodeId} onChange={onRootNodeChange}>
                                    {rootCandidateNodes.length ? (
                                        rootCandidateNodes.map((node) => (
                                            <option key={node.id} value={String(node.id)}>
                                                {node.path_cache}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="">선택 가능한 .tex 문서 없음</option>
                                    )}
                                </SettingsSelect>
                                <SettingsHint>
                                    {rootCandidateNodes.length
                                        ? ".tex 문서만 주 문서로 선택할 수 있습니다."
                                        : "먼저 .tex 문서를 생성한 뒤 주 문서를 설정할 수 있습니다."}
                                </SettingsHint>
                            </SettingsRow>
                            <SettingsRow>
                                <SettingsLabel>컴파일러</SettingsLabel>
                                <SettingsSelect value={settingsCompiler} onChange={onCompilerChange}>
                                    {compilerOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </SettingsSelect>
                            </SettingsRow>
                            <Actions>
                                <ActionButton
                                    type="button"
                                    onClick={onSave}
                                    disabled={isSaving || !rootCandidateNodes.length}
                                    $primary
                                >
                                    저장
                                </ActionButton>
                            </Actions>
                        </SettingsForm>
                    ) : null}
                </DialogBody>
            </Dialog>
        </Overlay>
    );
}

const SettingsTabs = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 0 10px 0;
`;

const SettingsTabButton = styled.button`
    height: 30px;
    padding: 0 10px;
    border: 1px solid ${(props) => (props.$active ? "#0f8f6f" : "#d6e0ea")};
    border-radius: 7px;
    background: ${(props) => (props.$active ? "#e7faf3" : "#ffffff")};
    color: ${(props) => (props.$active ? "#0f8f6f" : "#32485e")};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;

const SettingsForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SettingsRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const SettingsLabel = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: #253a50;
`;

const SettingsSelect = styled.select`
    height: 34px;
    padding: 0 10px;
    border: 1px solid #d5dfea;
    border-radius: 8px;
    background: #ffffff;
    color: #24384d;
    font-size: 13px;
`;

const SettingsHint = styled.div`
    font-size: 11px;
    color: #5d7389;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

export default ManuscriptSettingsModal;
